<script setup lang="ts">
import { useAppState } from '@src/store/ocg-app-state';
import { computed } from 'vue';

defineProps<{
  targetView?: string;
}>();

const appState = useAppState();

const logoURL = computed(() => {
  try {
    return appState.getCurrentLogo();
  }
  catch {
    return '/logos/a19_master_light.svg';
  }
});

// 8 dots: 4 in main color (--md-primary: #00bcd4) and 4 in tertiary color (--md-tertiary: #e91e63)
const dots = [
  { id: 1, index: 0, colorClass: 'dot-primary' },
  { id: 2, index: 1, colorClass: 'dot-primary' },
  { id: 3, index: 2, colorClass: 'dot-primary' },
  { id: 4, index: 3, colorClass: 'dot-primary' },
  { id: 5, index: 4, colorClass: 'dot-tertiary' },
  { id: 6, index: 5, colorClass: 'dot-tertiary' },
  { id: 7, index: 6, colorClass: 'dot-tertiary' },
  { id: 8, index: 7, colorClass: 'dot-tertiary' },
];
</script>

<template>
  <div class="analysis-skeleton-container" aria-busy="true" aria-live="polite">
    <div class="skeleton-panel">
      <!-- Centered brand logo & 8-dot dual-color blurred spinner -->
      <div class="skeleton-center-content">
        <div class="logo-wrapper">
          <img
            :src="logoURL"
            alt="AREA 19"
            class="skeleton-logo"
            draggable="false"
          >
        </div>

        <div class="spinner-wrapper" aria-hidden="true">
          <div class="dots-spinner">
            <div
              v-for="dot in dots"
              :key="dot.id"
              class="spinner-dot"
              :class="dot.colorClass"
              :style="{
                '--dot-index': dot.index,
                '--dot-angle': `${dot.index * 45}deg`,
              }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.analysis-skeleton-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--md-surface, #141414);
  overflow: hidden;
  display: flex;
  box-sizing: border-box;
  border-radius: 12px;
}

/* ── Page-wide Skeleton Panel (Single unified item) ── */
.skeleton-panel {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--md-surface-container-low, #1e1e1e);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Shimmer Wave Animation across the full item ── */
.skeleton-wave {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    90deg,
    transparent 0%,
    transparent 30%,
    rgba(255, 255, 255, 0.01) 42%,
    rgba(0, 188, 212, 0.01) 50%,
    rgba(233, 30, 99, 0.01) 56%,
    rgba(255, 255, 255, 0.01) 64%,
    transparent 74%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: shimmer-wave-sweep 3s infinite cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

@keyframes shimmer-wave-sweep {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* ── Subtle Central Ambient Radial Glow ── */
.skeleton-ambient-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 188, 212, 0.17) 0%, rgba(233, 30, 99, 0.2) 45%, transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  animation: ambient-pulse 1s ease-in-out infinite alternate;
}

@keyframes ambient-pulse {
  0% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(0.92);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.08);
  }
}

/* ── Center Content ── */
.skeleton-center-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-logo {
  width: 20vw;
  height: auto;
  max-width: 80vw;
  object-fit: contain;
  opacity: 0.6;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.35));
  user-select: none;
  pointer-events: none;
  animation: logo-gentle-breathe 3.6s ease-in-out infinite alternate;
}

@keyframes logo-gentle-breathe {
  0% {
    opacity: 0.88;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

/* ── 8-Dot Blurred Loading Spinner right under the logo ── */
.spinner-wrapper {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dots-spinner {
  --orbit-radius: 32px;
  position: relative;
  width: 120px;
  height: 120px;
  animation: spinner-spin 5s linear infinite;
  will-change: transform;
}

@keyframes spinner-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinner-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 13px;
  height: 13px;
  margin: -6.5px 0 0 -6.5px;
  border-radius: 50%;
  filter: blur(1px);
  transform: rotate(var(--dot-angle)) translate(var(--orbit-radius, 32px));
  will-change: transform, opacity;
  animation: dot-glow-pulse 3s ease-in-out infinite;
  animation-delay: calc(var(--dot-index) * -0.375s);
}

@keyframes dot-glow-pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: rotate(var(--dot-angle)) translate(var(--orbit-radius, 32px)) scale(0.75);
  }
  50% {
    opacity: 1;
    transform: rotate(var(--dot-angle)) translate(var(--orbit-radius, 32px)) scale(1.25);
  }
}

/* 4 Dots in Main/Primary Color (#00bcd4) */
.dot-primary {
  background-color: var(--md-primary);
  box-shadow:
    0 0 10px var(--md-primary),
    0 0 20px rgba(0, 188, 212, 0.4);
}

/* 4 Dots in Tertiary Color (#e91e63) */
.dot-tertiary {
  background-color: var(--md-tertiary);
  box-shadow:
    0 0 10px var(--md-tertiary),
    0 0 20px rgba(233, 30, 99, 0.4);
}
</style>
